import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useRecaptcha } from '../../../hooks/useRecaptcha';
import { regionsApi, Region } from '../../../api/regions';
import { RegistrationFormData, FormErrors, PasswordStrength } from '../types';

interface RegistrationFormProps {
  language: 'en' | 'sw';
  onSuccess: (data: { email?: string; phone?: string; otpType?: string }) => void;
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({ language, onSuccess }) => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useTranslation();
  const { getToken: getRecaptchaToken, isReady: recaptchaReady } = useRecaptcha();
  const [formData, setFormData] = useState<RegistrationFormData>({
    fullName: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
    gender: '',
    role: 'youth',
    region: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [regions, setRegions] = useState<{ value: string; label: string }[]>([]);
  const [loadingRegions, setLoadingRegions] = useState(true);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        setLoadingRegions(true);
        const data = await regionsApi.getRegions();
        const list = Array.isArray(data) ? data : (data as any)?.data ?? [];
        setRegions((list as Region[]).map((r) => ({ value: r.name, label: r.name })));
      } catch (e) {
        console.error('Failed to load regions:', e);
      } finally {
        setLoadingRegions(false);
      }
    };
    fetchRegions();
  }, []);

  const content = {
    en: {
      fullName: 'Full Name',
      fullNamePlaceholder: 'Enter your full name',
      email: 'Email Address',
      emailPlaceholder: 'Enter your email',
      phoneNumber: 'Phone Number',
      phoneNumberPlaceholder: '+255 700 000 000',
      dateOfBirth: 'Date of Birth',
      dateOfBirthPlaceholder: 'Select your date of birth',
      gender: 'Gender',
      genderPlaceholder: 'Select your gender',
      genderMale: 'Male',
      genderFemale: 'Female',
      genderOther: 'Other',
      location: 'Location',
      locationPlaceholder: 'Select your region',
      locationRequired: 'Location is required',
      role: 'Select Your Role',
      roleDescription: 'Choose the role that best describes you',
      password: 'Password',
      passwordPlaceholder: 'Create a strong password',
      confirmPassword: 'Confirm Password',
      confirmPasswordPlaceholder: 'Re-enter your password',
      agreeToTerms: 'I agree to the Terms of Service and Privacy Policy',
      createAccount: 'Create Account',
      fullNameRequired: 'Full name is required',
      emailRequired: 'Email is required',
      invalidEmail: 'Please enter a valid email address',
      phoneRequired: 'Phone number is required',
      invalidPhone: 'Please enter a valid phone number',
      dateOfBirthRequired: 'Date of birth is required',
      dateOfBirthInvalid: 'Please enter a valid date of birth',
      ageMinimum: 'You must be at least 16 years old',
      genderRequired: 'Gender is required',
      passwordRequired: 'Password is required',
      passwordWeak: 'Password must be at least 8 characters with uppercase, lowercase, and numbers',
      confirmPasswordRequired: 'Please confirm your password',
      passwordMismatch: 'Passwords do not match',
      termsRequired: 'You must agree to the terms and conditions',
      passwordStrengthWeak: 'Weak',
      passwordStrengthMedium: 'Medium',
      passwordStrengthStrong: 'Strong',
    },
    sw: {
      fullName: 'Jina Kamili',
      fullNamePlaceholder: 'Weka jina lako kamili',
      email: 'Barua Pepe',
      emailPlaceholder: 'Weka barua pepe yako',
      phoneNumber: 'Nambari ya Simu',
      phoneNumberPlaceholder: '+255 700 000 000',
      dateOfBirth: 'Tarehe ya Kuzaliwa',
      dateOfBirthPlaceholder: 'Chagua tarehe ya kuzaliwa',
      gender: 'Jinsia',
      genderPlaceholder: 'Chagua jinsia yako',
      genderMale: 'Mwanaume',
      genderFemale: 'Mwanamke',
      genderOther: 'Nyingine',
      location: 'Mahali',
      locationPlaceholder: 'Chagua mkoa wako',
      locationRequired: 'Mahali linahitajika',
      role: 'Chagua Jukumu Lako',
      roleDescription: 'Chagua jukumu linalokufaa zaidi',
      password: 'Nywila',
      passwordPlaceholder: 'Unda nywila imara',
      confirmPassword: 'Thibitisha Nywila',
      confirmPasswordPlaceholder: 'Weka nywila tena',
      agreeToTerms: 'Nakubali Masharti ya Huduma na Sera ya Faragha',
      createAccount: 'Fungua Akaunti',
      fullNameRequired: 'Jina kamili linahitajika',
      emailRequired: 'Barua pepe inahitajika',
      invalidEmail: 'Tafadhali weka barua pepe sahihi',
      phoneRequired: 'Nambari ya simu inahitajika',
      invalidPhone: 'Tafadhali weka nambari ya simu sahihi',
      dateOfBirthRequired: 'Tarehe ya kuzaliwa inahitajika',
      dateOfBirthInvalid: 'Tafadhali weka tarehe sahihi ya kuzaliwa',
      ageMinimum: 'Lazima uwe na umri wa miaka 16 au zaidi',
      genderRequired: 'Jinsia inahitajika',
      passwordRequired: 'Nywila inahitajika',
      passwordWeak: 'Nywila lazima iwe na herufi 8 au zaidi na herufi kubwa, ndogo na nambari',
      confirmPasswordRequired: 'Tafadhali thibitisha nywila yako',
      passwordMismatch: 'Nywila hazifanani',
      termsRequired: 'Lazima ukubali masharti na hali',
      passwordStrengthWeak: 'Dhaifu',
      passwordStrengthMedium: 'Wastani',
      passwordStrengthStrong: 'Imara',
    },
  };

  // Ensure language is valid, fallback to 'en' if not
  const validLanguage = (language === 'en' || language === 'sw') ? language : 'en';
  const localT = content[validLanguage] || content.en; // Fallback to English if language is invalid

  const calculatePasswordStrength = (password: string): PasswordStrength => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    if (score <= 2) {
      return { score, label: localT.passwordStrengthWeak, color: 'bg-error' };
    } else if (score <= 3) {
      return { score, label: localT.passwordStrengthMedium, color: 'bg-warning' };
    } else {
      return { score, label: localT.passwordStrengthStrong, color: 'bg-success' };
    }
  };

  const passwordStrength = calculatePasswordStrength(formData.password);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = localT.fullNameRequired;
    }

    if (!formData.email.trim()) {
      newErrors.email = localT.emailRequired;
    } else if (!/^[^\s@]+@([^\s@]+\.)+[^\s@]+$|^[^\s@]+@localhost$/.test(formData.email)) {
      newErrors.email = localT.invalidEmail;
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = localT.phoneRequired;
    } else if (!/^\+?[0-9]{10,15}$/.test(formData.phoneNumber.replace(/\s/g, ''))) {
      newErrors.phoneNumber = localT.invalidPhone;
    }

    // Validate date of birth
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = localT.dateOfBirthRequired;
    } else {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;
      
      if (isNaN(birthDate.getTime())) {
        newErrors.dateOfBirth = localT.dateOfBirthInvalid;
      } else if (actualAge < 16) {
        newErrors.dateOfBirth = localT.ageMinimum;
      } else if (birthDate > today) {
        newErrors.dateOfBirth = localT.dateOfBirthInvalid;
      }
    }

    // Validate gender
    if (!formData.gender) {
      newErrors.gender = localT.genderRequired;
    }

    if (!formData.region?.trim()) {
      newErrors.region = localT.locationRequired;
    }

    if (!formData.password) {
      newErrors.password = localT.passwordRequired;
    } else if (passwordStrength.score < 3) {
      newErrors.password = localT.passwordWeak;
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = localT.confirmPasswordRequired;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = localT.passwordMismatch;
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = localT.termsRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const recaptchaToken = recaptchaReady ? await getRecaptchaToken('register') : '';

      // Prepare registration data for API
      const registrationData: any = {
        name: formData.fullName,
        email: formData.email,
        date_of_birth: formData.dateOfBirth,
        gender: formData.gender,
        region: formData.region?.trim() || undefined,
        password: formData.password,
        password_confirmation: formData.confirmPassword,
        ...(recaptchaToken && { recaptcha_token: recaptchaToken }),
      };

      // Only add phone fields if phone number is provided
      if (formData.phoneNumber) {
        registrationData.phone = formData.phoneNumber.replace(/\s/g, '');
        registrationData.phone_country_code = '+255'; // Default to Tanzania
      }

      // Remove only undefined values (not empty strings for required fields)
      Object.keys(registrationData).forEach(key => {
        if (registrationData[key] === undefined) {
          delete registrationData[key];
        }
      });

      await register(registrationData);
      
      // Show success toast message
      toast.success(
        language === 'sw' 
          ? 'Umesajiliwa kwa mafanikio! Karibu kwenye Jukwaa la Vijana.'
          : 'Registration successful! Welcome to Sisi ni Tanzania.',
        {
          position: 'top-center',
          autoClose: 10000,
        }
      );
      
      // If registration successful and user is logged in, navigate to dashboard
      // Otherwise, show OTP verification
      const successData: { email?: string; phone?: string; otpType?: string } = {};
      
      if (formData.email && formData.phoneNumber) {
        successData.email = formData.email;
        successData.phone = formData.phoneNumber.replace(/\s/g, '');
        successData.otpType = 'both';
      } else if (formData.email) {
        successData.email = formData.email;
        successData.otpType = 'email';
      } else if (formData.phoneNumber) {
        successData.phone = formData.phoneNumber.replace(/\s/g, '');
        successData.otpType = 'phone';
      }
      
      if (successData.email || successData.phone) {
        onSuccess(successData);
      } else {
        navigate('/timeline');
      }
    } catch (error: any) {
      // Check if it's a duplicate account error (generic message from backend)
      const errorMessage = error?.message || error?.response?.data?.message || '';
      const isDuplicateError = errorMessage.includes('already in use') || 
                                errorMessage.includes('already registered') ||
                                errorMessage.includes('already exists') ||
                                (error?.response?.status === 422 && errorMessage.includes('Try resetting'));

      if (isDuplicateError) {
        // Show generic toast message for duplicate account
        const genericMessage = errorMessage || 'Email, Phone or Username is already in use. Try resetting your password.';
        toast.error(genericMessage, {
          position: 'top-center',
          autoClose: 10000,
        });
        setErrors({ form: genericMessage });
      } else if (error?.errors && typeof error.errors === 'object') {
        // Handle other validation errors from API
        const apiErrors: FormErrors = {};
        // Map API error keys to form error keys
        Object.keys(error.errors).forEach((key) => {
          const errorMessages = error.errors[key];
          if (Array.isArray(errorMessages) && errorMessages.length > 0) {
            // Map backend field names to frontend field names
            const fieldMap: Record<string, keyof FormErrors> = {
              name: 'fullName',
              email: 'email',
              phone: 'phoneNumber',
              date_of_birth: 'dateOfBirth',
              gender: 'gender',
              region: 'region',
              password: 'password',
              password_confirmation: 'confirmPassword',
              recaptcha: 'form',
            };
            const frontendField = fieldMap[key] || key;
            apiErrors[frontendField] = errorMessages[0];
          }
        });
        setErrors(apiErrors);
      } else {
        // Generic error
        const fallbackMessage = error instanceof Error ? error.message : t('auth.registration_failed');
        toast.error(fallbackMessage, {
          position: 'top-center',
          autoClose: 10000,
        });
        setErrors({ form: fallbackMessage });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Input
        type="text"
        label={localT.fullName}
        placeholder={localT.fullNamePlaceholder}
        value={formData.fullName}
        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
        error={errors.fullName}
        required
      />

      <Input
        type="email"
        label={localT.email}
        placeholder={localT.emailPlaceholder}
        value={formData.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        error={errors.email}
        required
      />

      <Input
        type="tel"
        label={localT.phoneNumber}
        placeholder={localT.phoneNumberPlaceholder}
        value={formData.phoneNumber}
        onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
        error={errors.phoneNumber}
        required
      />

      <Input
        type="date"
        label={localT.dateOfBirth}
        placeholder={localT.dateOfBirthPlaceholder}
        value={formData.dateOfBirth}
        onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
        error={errors.dateOfBirth}
        required
        max={new Date(new Date().setFullYear(new Date().getFullYear() - 16)).toISOString().split('T')[0]}
      />

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          {localT.gender} <span className="text-destructive">*</span>
        </label>
        <select
          value={formData.gender}
          onChange={(e) => setFormData({ ...formData, gender: e.target.value as 'male' | 'female' | '' })}
          className={`w-full px-3 py-2 border rounded-md bg-background text-foreground ${
            errors.gender ? 'border-destructive' : 'border-border'
          } focus:outline-none focus:ring-2 focus:ring-primary`}
          required
        >
          <option value="">{localT.genderPlaceholder}</option>
          <option value="male">{localT.genderMale}</option>
          <option value="female">{localT.genderFemale}</option>
        </select>
        {errors.gender && (
          <p className="mt-1 text-sm text-destructive">{errors.gender}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          {localT.location} <span className="text-destructive">*</span>
        </label>
        <Select
          value={formData.region}
          onChange={(value) => setFormData({ ...formData, region: (value as string) || '' })}
          options={[{ value: '', label: `— ${localT.locationPlaceholder} —` }, ...regions]}
          placeholder={loadingRegions ? localT.locationPlaceholder : localT.locationPlaceholder}
          disabled={loadingRegions}
          required
          error={errors.region}
        />
        {errors.region && (
          <p className="mt-1 text-sm text-destructive">{errors.region}</p>
        )}
      </div>

      <div>
        <div className="relative">
          <Input
            type={showPassword ? 'text' : 'password'}
            label={localT.password}
            placeholder={localT.passwordPlaceholder}
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            error={errors.password}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-fast"
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>
        {formData.password && (
          <div className="mt-2">
            <div className="flex items-center gap-2 mb-1">
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full ${passwordStrength.color} transition-smooth`}
                  style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                />
              </div>
              <span className="text-xs font-medium text-muted-foreground">{passwordStrength.label}</span>
            </div>
          </div>
        )}
      </div>

      <div className="relative">
        <Input
          type={showConfirmPassword ? 'text' : 'password'}
          label={localT.confirmPassword}
          placeholder={localT.confirmPasswordPlaceholder}
          value={formData.confirmPassword}
          onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
          error={errors.confirmPassword}
          required
        />
        <button
          type="button"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-fast"
        >
          <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
        </button>
      </div>

      <Checkbox
        label={localT.agreeToTerms}
        checked={formData.agreeToTerms}
        onChange={(checked) => setFormData({ ...formData, agreeToTerms: checked })}
        error={errors.agreeToTerms}
        required
      />

      <Button type="submit" variant="default" fullWidth loading={isLoading} iconName="UserPlus" iconPosition="right">
        {localT.createAccount}
      </Button>
    </form>
  );
};

export default RegistrationForm;